import axios from "axios";
import app from "./app";
import getenvvar from "../utils/Env";

const loginFlow = (appToken, remember) => {
  // A freshly issued session token supersedes any TFA handshake left pending
  // in this tab (e.g. a TFA screen that was abandoned).
  sessionStorage.removeItem("tfaToken");

  if (remember) {
    localStorage.setItem("appToken", appToken);
    localStorage.setItem("user_locale", "en"); // set default value
  } else {
    localStorage.removeItem("appToken");
    sessionStorage.setItem("appToken", appToken);
    sessionStorage.setItem("user_locale", "en"); // set default value
  }
};

const tfaVerifyFlow = tfaToken => {
  // A new login supersedes any previous session in this browser, and the
  // backend has just invalidated this user's PF token when issuing the TFA
  // one. An app token left in storage would be preferred by getAuthToken()
  // on /verify, and its rejection would clear the pending TFA challenge.
  localStorage.removeItem("appToken");
  sessionStorage.removeItem("appToken");
  sessionStorage.setItem("tfaToken", tfaToken);
};

export async function login(user, pass, remember) {
  const baseURL = getenvvar("REACT_APP_API_BASE_URL");

  const axiosInstance = axios.create({
    baseURL: baseURL,
    headers: {
      "Content-Type": "application/json",
      Accept: "*/*"
    }
  });
  axiosInstance.interceptors.response.use(
    response => {
      return response;
    },
    error => {
      return Promise.reject(error?.response?.data || error);
    }
  );

  axiosInstance.interceptors.request.use(
    config => {
      config.headers["Accept-Language"] = localStorage.getItem("user_locale");
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  return axiosInstance
    .post(baseURL + "/api-token-auth/", {
      username: user,
      password: pass
    })
    .then(res => {
      if (res.status === 200) {
        if (res.data.app_token) {
          loginFlow(res.data.app_token, remember);

          return [false, res.data.app_token];
        } else if (res.data.tfa_token) {
          tfaVerifyFlow(res.data.tfa_token);

          return [true, res.data.tfa_token];
        }
      } else {
        return { status: 404 };
      }
    })
    .catch(err => {
      return Promise.reject(err);
    });
}

/**
 * Exchange a short-lived, single-use SSO token for a final Pathfinder
 * auth token. The SSO token is sent in the request body (the endpoint is
 * AllowAny and treats the body token as the credential), so this uses a
 * bare axios instance rather than the shared authenticated `app` instance,
 * which would attach an Authorization header.
 *
 * On success the final token is persisted as `appToken` in sessionStorage
 * (session-only) via `loginFlow`.
 *
 * @param { string } ssoToken - The short-lived key from the SSO redirect URL.
 * @returns { Promise<string> } Resolves with the final app token.
 */
export async function ssoTokenExchange(ssoToken) {
  const baseURL = getenvvar("REACT_APP_API_BASE_URL");

  const axiosInstance = axios.create({
    baseURL: baseURL,
    headers: {
      "Content-Type": "application/json",
      Accept: "*/*"
    }
  });
  axiosInstance.interceptors.response.use(
    response => {
      return response;
    },
    error => {
      return Promise.reject(error?.response?.data || error);
    }
  );

  return axiosInstance
    .post(baseURL + "/api-token-auth/sso-exchange/", {
      sso_token: ssoToken
    })
    .then(res => {
      // Backend returns { user, token, api_token }. The FINAL token is
      // `token` (NOT `app_token`). `api_token` is unused by the frontend.
      loginFlow(res.data.token, false);
      return res.data.token;
    });
}

export function getProfile() {
  return app.get("/users/user-profile/", {}).then(res => {
    return res.data;
  });
}
export function updateProfile(id, data) {
  return app.patch("/users/user-profile/" + id + "/", data).then(res => {
    return res.data;
  });
}

/**
 * Method to delete auth token from BE when logout
 * @returns
 */
export function deleteAuthToken() {
  return app.post("/api-token-logout/").then(res => {
    return res.data;
  });
}
